/**
 * Die Fassungen der App und was sie gebracht haben.
 *
 * **Diese Liste ist die Fassungsnummer.** `APP_VERSION` ist die Version ihres obersten
 * Eintrags — es gibt keine zweite Stelle, an der eine Nummer gepflegt würde und die
 * hinter der Auslieferung zurückbleiben könnte. Die Angabe in `package.json` wird beim
 * Build dagegen geprüft (`scripts/build-version.ts`) und bricht ab, wenn sie abweicht.
 *
 * Bewusst ohne DOM- und ohne Datenbankzugriff: Gelesen wird die Datei von der
 * Oberfläche, vom Service Worker (der hat kein `window`) und vom Build-Skript unter
 * Node. Dasselbe Vorgehen wie bei `@/features/updates/diff`.
 *
 * ── Eine neue Fassung veröffentlichen ───────────────────────────────────────────
 *
 * 1. Eintrag oben in `FASSUNGEN` anlegen — neueste zuerst.
 * 2. `version` in `package.json` auf dieselbe Nummer setzen.
 * 3. Ausliefern. Den Rest erledigt die Pipeline.
 *
 * Welche Stelle hochgeht, entscheidet die Wirkung auf den Nutzer, nicht der Umfang
 * des Umbaus:
 *
 * | Stelle  | wann                                             | Push | Hinweis beim Öffnen |
 * |---------|--------------------------------------------------|------|---------------------|
 * | Major   | die App bedient sich grundlegend anders          | ja   | ja                  |
 * | Minor   | neue Funktionen, alles Bisherige bleibt          | ja   | ja                  |
 * | Patch   | Fehlerbehebungen, Feinschliff                    | nein | nein                |
 *
 * Ein Eintrag ohne `neu`-Zeile erzeugt keinen Hinweis beim Öffnen — es gibt dann
 * nichts zu zeigen, und ein Dialog, der nur sich selbst ankündigt, hält auf.
 */

/**
 * Art einer Änderung.
 *
 * Drei Arten und nicht mehr: Sie beantworten die einzige Frage, die der Leser hat —
 * kann ich jetzt etwas Neues, ist etwas Vertrautes besser geworden, oder war etwas
 * kaputt und ist es jetzt nicht mehr.
 */
export type Art = 'neu' | 'verbessert' | 'behoben'

export interface Aenderung {
  art: Art
  /**
   * Ein vollständiger Satz ohne Schlusspunkt, aus Sicht des Nutzers. Nicht „Refactoring
   * des Viewer-Stores“, sondern „Karte mit zwei Fingern zoomen und verschieben“.
   */
  text: string
}

export interface Fassung {
  /** SemVer, drei Stellen. */
  version: string
  /** Tag der Veröffentlichung, JJJJ-MM-TT. */
  datum: string
  aenderungen: Aenderung[]
}

/** Die Überschriften über den Gruppen einer Fassung. */
export const ART_LABELS: Record<Art, string> = {
  neu: 'Neu',
  verbessert: 'Verbessert',
  behoben: 'Behoben',
}

/** Reihenfolge der Gruppen — das Neue zuerst, weil es den Leser hergeführt hat. */
export const ARTEN: readonly Art[] = ['neu', 'verbessert', 'behoben']

/**
 * Neueste Fassung zuerst.
 *
 * Der Typ verlangt mindestens einen Eintrag: `APP_VERSION` liest den ersten, und eine
 * leere Liste hieße, dass die App keine Fassung hat.
 */
export const FASSUNGEN: readonly [Fassung, ...Fassung[]] = [
  {
    version: '1.11.0',
    datum: '2026-10-26',
    aenderungen: [
      {
        art: 'neu',
        text: 'Taschenkarten zur ersten Minute (San) und zum ABCDE-Schema (San)',
      },
    ],
  },
  {
    version: '1.10.0',
    datum: '2026-10-08',
    aenderungen: [
      {
        art: 'neu',
        text: 'Taschenkarte Übermittlungszeichen – Sicht- und Handzeichen zur Verständigung ohne Funk',
      },
    ],
  },
  {
    version: '1.9.1',
    datum: '2026-10-06',
    aenderungen: [
      {
        art: 'behoben',
        text: 'Taschenkarte PTZ: fehlende Qualifikation (C1) für den Kraftfahrer des ELW ergänzt',
      },
    ],
  },
  {
    version: '1.9.0',
    datum: '2026-10-02',
    aenderungen: [
      {
        art: 'neu',
        text: 'Taschenkarte PAL Alarmierung-Checkliste für Gruppenführer',
      },
    ],
  },
  {
    version: '1.8.0',
    datum: '2026-09-30',
    aenderungen: [
      {
        art: 'neu',
        text: 'Taschenkarten zu Taktischen Einheiten und Alarmarten Hamburg',
      },
    ],
  },
  {
    version: '1.7.0',
    datum: '2026-09-25',
    aenderungen: [
      {
        art: 'neu',
        text: 'Taschenkarten zu DIVERA-Alarmierung, Dienststellungsabzeichen und Behandlungsstelle GW-San',
      },
      { art: 'verbessert', text: 'Taschenkarte PTZ um Funkrufnamen für Zugführer, Gruppenführer und RMHP ergänzt' },
    ],
  },
  {
    version: '1.6.2',
    datum: '2026-09-17',
    aenderungen: [
      {
        art: 'behoben',
        text: 'Karten öffnen wieder von oben, statt an der Scrollposition der zuvor geöffneten Liste',
      },
    ],
  },
  {
    version: '1.6.1',
    datum: '2026-09-17',
    aenderungen: [
      {
        art: 'behoben',
        text: 'Taschenkarten PTZ, Funkrufnamen und Funkstatus inhaltlich korrigiert',
      },
    ],
  },
  {
    version: '1.6.0',
    datum: '2026-09-16',
    aenderungen: [
      { art: 'neu', text: 'Taschenkarte zur Sauerstoffgabe mit Applikationssystemen und Reichweitenberechnung' },
    ],
  },
  {
    version: '1.5.0',
    datum: '2026-09-16',
    aenderungen: [
      { art: 'neu', text: 'Taschenkarte mit Normwerten zu Atmung, Puls, Blutdruck, Blutzucker, SpO2 und Temperatur' },
    ],
  },
  {
    version: '1.4.0',
    datum: '2026-09-16',
    aenderungen: [
      {
        art: 'neu',
        text: 'Taschenkarten zur 9er-Regel bei Verbrennungen und zu den Funkstatus-Meldungen',
      },
    ],
  },
  {
    version: '1.3.0',
    datum: '2026-09-14',
    aenderungen: [
      {
        art: 'neu',
        text: 'Taschenkarten zu Funkrufnamen, Vorsichtung nach PRIOR-Algorithmus und PTZ-ELW-Transportaufträgen',
      },
      { art: 'verbessert', text: 'Taschenkarten PTZ, GW-San-Zelt und SG30-Zelt inhaltlich aktualisiert, PTZ-Funkrufnamen vervollständigt' },
    ],
  },
  {
    version: '1.2.2',
    datum: '2026-09-14',
    aenderungen: [
      { art: 'verbessert', text: 'Knopf zum Drehen der Karte entfernt, da alle Taschenkarten im Hochformat angelegt sind' },
    ],
  },
  {
    version: '1.2.1',
    datum: '2026-09-13',
    aenderungen: [
      {
        art: 'verbessert',
        text: 'Taschenkarten PTZ, GW-San-Zelt und SG30-Zelt auf finale Fassung aktualisiert',
      },
    ],
  },
  {
    version: '1.2.0',
    datum: '2026-09-13',
    aenderungen: [
      { art: 'neu', text: 'Taschenkarten zum Verladen und Aufbau der Sanitätszelte (GW-San, SG30)' },
    ],
  },
  {
    version: '1.1.0',
    datum: '2026-09-13',
    aenderungen: [
      { art: 'neu', text: 'Karten der Bereitschaften als eigene Organisation filterbar' },
    ],
  },
  {
    version: '1.0.2',
    datum: '2026-09-12',
    aenderungen: [
      {
        art: 'verbessert',
        text: 'Die Suche reagiert flüssiger, bevor ein Stichwort eingegeben ist',
      },
    ],
  },
  {
    version: '1.0.1',
    datum: '2026-09-12',
    aenderungen: [
      {
        art: 'verbessert',
        text: 'Der Änderungsverlauf öffnet als Blatt über der Ansicht statt als eigene Seite',
      },
      {
        art: 'behoben',
        text: 'Geänderte Karten und neue Fassungen werden auch bemerkt, wenn die App aus dem Hintergrund zurückkommt',
      },
    ],
  },
  {
    version: '1.0.0',
    datum: '2026-09-12',
    aenderungen: [
      { art: 'neu', text: 'Alle Änderungen der App unter „Mehr“ nachlesen' },
      { art: 'neu', text: 'Nach dem Einspielen einer Fassung nennt die App ihre neuen Funktionen' },
      { art: 'neu', text: 'Benachrichtigung, sobald eine Fassung mit neuen Funktionen bereitsteht' },
    ],
  },
  {
    version: '0.3.0',
    datum: '2026-09-12',
    aenderungen: [
      { art: 'neu', text: 'Favoriten wahlweise als Kacheln oder als Liste, sortierbar durch Ziehen' },
      { art: 'neu', text: 'Karte mit zwei Fingern zoomen und verschieben' },
      { art: 'verbessert', text: 'Die Suche legt sich als Blatt über die Ansicht, die Filter sind anzutippen' },
      { art: 'verbessert', text: 'Das Erscheinungsbild steht als eine Leiste statt als vier Kacheln' },
      { art: 'behoben', text: 'Kein doppelter Hinweis mehr auf der Startseite, wenn keine Karte geladen ist' },
    ],
  },
  {
    version: '0.2.0',
    datum: '2026-09-12',
    aenderungen: [
      { art: 'neu', text: 'Einsprungpunkte am App-Symbol führen direkt in Suche und Katalog' },
      { art: 'neu', text: 'Eigene Startbilder auf iPhone und iPad statt weißer Fläche beim Öffnen' },
      { art: 'verbessert', text: 'Gerundete Kanten und eine eigene Rubrikleiste je Organisation' },
      { art: 'verbessert', text: 'Hinweise erscheinen als Dialog am unteren Rand, in Daumenreichweite' },
    ],
  },
  {
    version: '0.1.0',
    datum: '2026-09-11',
    aenderungen: [
      { art: 'neu', text: 'Katalog aller Taschenkarten, nach Kategorien geordnet' },
      { art: 'neu', text: 'Volltextsuche über alle Karten, auch über zusammengesetzte Wörter' },
      { art: 'neu', text: 'Favoriten und zuletzt geöffnete Karten auf der Startseite' },
      { art: 'neu', text: 'Den gesamten Kartenbestand auf einen Knopfdruck offline verfügbar machen' },
      { art: 'neu', text: 'Hinweis und Benachrichtigung, sobald Karten hinzukommen oder überarbeitet werden' },
      { art: 'neu', text: 'Drei Erscheinungsbilder, darunter ein Rotlichtmodus für die Dunkelheit' },
    ],
  },
]

/** Die Fassung, die gerade läuft. */
export const APP_VERSION: string = FASSUNGEN[0].version

/**
 * Die drei Stellen einer Fassungsnummer.
 *
 * Fehlende oder unleserliche Stellen werden zu 0. Die Nummern stammen aus dieser Datei
 * und aus `version.json` vom eigenen Server — eine Fehlerbehandlung mit Ausnahmen hätte
 * hier nichts zu tun, außer im Service Worker eine Meldung zu verhindern.
 */
function stellen(version: string): [number, number, number] {
  const teile = version.split('.').map((teil) => Number.parseInt(teil, 10))

  return [teile[0] || 0, teile[1] || 0, teile[2] || 0]
}

/** Negativ, wenn `a` älter ist als `b`; 0 bei Gleichstand; positiv, wenn `a` neuer ist. */
export function vergleiche(a: string, b: string): number {
  const [aMajor, aMinor, aPatch] = stellen(a)
  const [bMajor, bMinor, bPatch] = stellen(b)

  // Stellenweise und nicht als Zeichenkette: Als Text sortiert stünde „1.10.0“ vor
  // „1.9.0“ — ein Fehler, der erst beim zehnten Minor-Schritt aufträte und dann alle
  // Meldungen verstummen ließe.
  return aMajor - bMajor || aMinor - bMinor || aPatch - bPatch
}

/**
 * Unterscheiden sich zwei Fassungen nur in der Patch-Stelle?
 *
 * Entscheidet, ob eine Auslieferung eine Push-Nachricht auslöst. Dieselbe Regel wendet
 * `api/notify.php` auf dem Server an — beide müssen gleich rechnen, sonst meldet der
 * Server etwas, das die App anschließend nicht als Neuerung zeigen kann.
 */
export function istNurPatch(alt: string, neu: string): boolean {
  const [altMajor, altMinor] = stellen(alt)
  const [neuMajor, neuMinor] = stellen(neu)

  return altMajor === neuMajor && altMinor === neuMinor
}

/** Alle Fassungen, die neuer sind als die angegebene — neueste zuerst. */
export function fassungenSeit(version: string): Fassung[] {
  return FASSUNGEN.filter((fassung) => vergleiche(fassung.version, version) > 0)
}

/**
 * Die neuen Funktionen, die seit der angegebenen Fassung hinzugekommen sind.
 *
 * Bewusst nur `neu`: Der Hinweis beim Öffnen hält den Nutzer auf, und das darf nur,
 * was er anschließend tun kann. Verbesserungen und Fehlerbehebungen merkt er im
 * Gebrauch; wer sie nachlesen will, findet sie vollständig unter „Mehr“.
 */
export function neueFunktionen(seit: string): Aenderung[] {
  return fassungenSeit(seit).flatMap((fassung) =>
    fassung.aenderungen.filter((aenderung) => aenderung.art === 'neu'),
  )
}

/** Datum einer Fassung, ausgeschrieben wie im Rest der App. */
export function formatiereDatum(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}
